"use client";

import { ModalStackDepthReporter } from "@klarify/ui/hooks/use-modal-registry";
import { cn } from "@klarify/ui/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { X } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Button } from "./button";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-black/40 backdrop-blur-xs data-[state=closed]:animate-out data-[state=open]:animate-in",
			className,
		)}
		{...props}
	/>
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentProps<
	typeof DialogPrimitive.Content
> & {
	onClose?: () => void;
	showCloseButton?: boolean;
	/** When true, renders content in-place without a portal or overlay. */
	inline?: boolean;
};

const DialogCloseButton = ({ onClose }: { onClose?: () => void }) => {
	return (
		<div className="absolute top-4 right-4 flex items-center justify-center">
			<DialogPrimitive.Close asChild>
				<Button
					variant={"outline"}
					rounding={"md"}
					size="icon"
					onClick={() => onClose?.()}
				>
					<Cross2Icon className="h-6 w-6 text-klarify-neutral-500" />
					<span className="sr-only">
						<Trans>Close</Trans>
					</span>
				</Button>
			</DialogPrimitive.Close>
		</div>
	);
};

// In-flow close button for dialogs that place the X inside their own header
// row (next to the title) instead of the absolute-positioned default above.
const DialogHeaderClose = ({
	className,
	...props
}: React.ComponentProps<typeof Button>) => {
	const { t } = useLingui();

	return (
		<DialogPrimitive.Close asChild>
			<Button
				type="button"
				variant="ghost"
				size="icon"
				rounding="md"
				className={cn(
					"h-9 w-9 shrink-0 border border-klarify-cloud-200 bg-klarify-cloud-100 shadow-none hover:bg-klarify-gray-mod-200",
					className,
				)}
				aria-label={t`Close`}
				{...props}
			>
				<X className="h-5 w-5" />
			</Button>
		</DialogPrimitive.Close>
	);
};
DialogHeaderClose.displayName = "DialogHeaderClose";

const PortaledDialogContent = ({
	ref,
	className,
	style,
	children,
	onClose,
	showCloseButton = true,
	...props
}: Omit<DialogContentProps, "inline">) => {
	// Depth comes from a reporter inside the Presence-gated content: it only
	// registers while the dialog is open, so closed-but-mounted dialogs never
	// count toward the open-modal registry (which would suppress the
	// product-update CTAs) and never skew stacked z-index offsets.
	const [depth, setDepth] = useState(0);
	const stackOffset = Math.max(depth - 1, 0) * 20;
	// Skip inline z-index for unstacked modals so child portals (Popover,
	// Select, DropdownMenu — all z-50) keep the same stacking baseline as
	// before and continue to win via DOM order.
	const overlayStyle =
		stackOffset > 0 ? { zIndex: 40 + stackOffset } : undefined;
	const contentStyle =
		stackOffset > 0 ? { ...style, zIndex: 50 + stackOffset } : style;
	return (
		<DialogPortal>
			<DialogOverlay style={overlayStyle} />
			<DialogPrimitive.Content
				ref={ref}
				className={cn(
					"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid max-h-screen w-full max-w-[95vw] translate-x-[-50%] translate-y-[-50%] gap-2 rounded-lg bg-klarify-cloud-50 p-3 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in",
					className,
				)}
				style={contentStyle}
				{...props}
			>
				<ModalStackDepthReporter onDepthChange={setDepth} />
				{children}
				{showCloseButton && <DialogCloseButton onClose={onClose} />}
			</DialogPrimitive.Content>
		</DialogPortal>
	);
};

const DialogContent = ({
	ref,
	className,
	children,
	onClose,
	showCloseButton = true,
	inline = false,
	...props
}: DialogContentProps) => {
	if (inline) {
		return (
			<DialogPrimitive.Content
				ref={ref}
				className={cn(
					"relative grid h-full w-full gap-2 rounded-lg bg-klarify-cloud-50 p-3",
					className,
				)}
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				{...props}
			>
				{children}
				{showCloseButton && <DialogCloseButton onClose={onClose} />}
			</DialogPrimitive.Content>
		);
	}

	return (
		<PortaledDialogContent
			ref={ref}
			className={className}
			onClose={onClose}
			showCloseButton={showCloseButton}
			{...props}
		>
			{children}
		</PortaledDialogContent>
	);
};
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.ComponentProps<"div">) => (
	<div
		className={cn("flex flex-col gap-1 text-center sm:text-left", className)}
		{...props}
	/>
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.ComponentProps<"div">) => (
	<div
		className={cn(
			"flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
			className,
		)}
		{...props}
	/>
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			"font-semibold text-klarify-neutral-800 text-lg leading-none tracking-tight",
			className,
		)}
		{...props}
	/>
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn("text-klarify-neutral-600 text-sm", className)}
		{...props}
	/>
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogHeaderClose,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
